import { inject, Injectable } from "@angular/core";
import { TokenIsExpiredError, TokensAndDecodedToken } from "../interfaces/token-error";
import { lastValueFrom, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { PcpLiveSessionToken } from "../interfaces/live-lesson-token";
import { PersistentTokenStorageService } from "./persistent-token-storage.service";
import { PcpTokenDecoderService } from "./pcp-token-decoder.service";
import { PcpLiveTokenDecoder } from "../live-auth-services/pcp-live-token-decoder.service";
import { ensureError } from "@pcp/core/util";
import { PCP_LOGGER } from "@pcp/logging";


const isValidString = (value: any): value is string => value && typeof value === 'string';

@Injectable({
    providedIn: 'root'
})
export class RestoreTokenHelperService {

    private readonly tokenStorage = inject(PersistentTokenStorageService);
    private readonly pcpTokenDecoder = inject(PcpTokenDecoderService);
    private readonly pcpLiveTokenDecoder = inject(PcpLiveTokenDecoder);
    private readonly log = inject(PCP_LOGGER).createScoped('auth');


    private logRefreshError(eventName: string, error: unknown, onFailed: (error: unknown) => void): void {
        const err = ensureError(error);
        this.log.error('Token refresh failed', err);
        this.log.trackEvent(eventName, {
            isOnline: navigator.onLine,
            errorType: err.constructor.name,
            errorMessage: err.message,
        });
        onFailed(error);
    }

    async restoreToken(params: {
        onRequested: (token: string, refreshToken: string) => void,
        onSuccess: (tokenData: TokensAndDecodedToken) => void,
        onFailed: (error: any) => void,
        performTokenRefresh: (token: string, refreshToken: string) => Observable<TokensAndDecodedToken>,
    }) {
        const token = this.tokenStorage.getAccessToken();
        const refreshToken = this.tokenStorage.getRefreshToken();

        if (isValidString(token) && isValidString(refreshToken)) {
            params.onRequested(token, refreshToken);
            try {
                const tokenData = this.pcpTokenDecoder.decodeAndValidateToken({ token, refreshToken });
                params.onSuccess(tokenData);

                return tokenData;
            } catch (error) {
                if (error instanceof TokenIsExpiredError) {
                    return lastValueFrom(params.performTokenRefresh(token, refreshToken).pipe(
                        map(tokenData => {
                            params.onSuccess(tokenData);

                            return tokenData;
                        }),
                        catchError(error => {
                            this.logRefreshError('TokenRefreshFailedDuringRestore', error, params.onFailed);
                            return of(undefined);
                        }),
                    ));
                }

                params.onFailed(error);

                return of(undefined);
            }
        } else {
            params.onFailed({ error: 'No token found to restore' });

            return Promise.resolve();
        }
    }

    async restoreLiveLessonToken(params: {
        onRequested: (token: string, refreshToken: string, liveLessonToken: string) => void,
        onSuccess: (tokenData: TokensAndDecodedToken, liveLessonToken: PcpLiveSessionToken) => void,
        onFailed: (error: any) => void,
        performTokenRefresh: (token: string, refreshToken: string) => Observable<TokensAndDecodedToken>,
    }) {
        const token = this.tokenStorage.getAccessToken();
        const refreshToken = this.tokenStorage.getRefreshToken();

        const liveLessonToken = this.tokenStorage.getLiveLessonToken();
        let decodedLiveToken: PcpLiveSessionToken;

        if (isValidString(liveLessonToken)) {
            decodedLiveToken = this.pcpLiveTokenDecoder.decodeToken(liveLessonToken);

        } else {
            params.onFailed({ error: 'No token found to restore' });

            return;
        }


        if (isValidString(token) && isValidString(refreshToken) && decodedLiveToken) {
            params.onRequested(token, refreshToken, liveLessonToken);
            try {
                const tokenData = this.pcpTokenDecoder.decodeAndValidateToken({ token, refreshToken });
                params.onSuccess(tokenData, decodedLiveToken);

                return tokenData;
            } catch (error) {
                const err = ensureError(error);
                const tokenIsExpired = error instanceof TokenIsExpiredError;
                const iatInTheFuture = err.message === 'Token iat is in the future';

                if (tokenIsExpired || iatInTheFuture) {
                    return lastValueFrom(params.performTokenRefresh(token,  refreshToken).pipe(
                        map(tokenData => {
                            params.onSuccess(tokenData, decodedLiveToken);

                            return tokenData;
                        }),
                        catchError(error => {
                            this.logRefreshError('LiveTokenRefreshFailedDuringRestore', error, params.onFailed);
                            return of(undefined);
                        }),
                    ));
                }

                params.onFailed(error);

                return of(undefined);
            }
        } else {
            params.onFailed({ error: 'No token found to restore' });

            return Promise.resolve();
        }
    }
}
